import { useState, useEffect, useMemo, type ReactNode, type CSSProperties } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../providers/TenantProvider';
import { Ic, ICON_PATHS } from '../lib/ui';
import { BLOQUE_MODULOS } from '../config/modules';
import { useOrganizacion } from '../providers/OrganizacionProvider';
import { useI18n } from '../i18n';
import type { Lang } from '../i18n';
import { QuickSearchModal } from '../modules/shared/components/QuickSearchModal';
import { NotificationBell } from '../modules/shared/components/NotificationBell';
import { AppVersionBadge } from '../modules/shared/components/AppVersionBadge';
import { UserMenu } from '../modules/shared/components/UserMenu';
import { ActionCenterBadge } from '../modules/platform/sign/ActionCenterBadge';
import { ImpersonationBanner } from '../modules/shared/components/ImpersonationBanner';

const SIDEBAR_EXPANDED = 240;
const SIDEBAR_COLLAPSED = 56;
const MOBILE_BREAKPOINT = 768;
const SIDEBAR_BG = '#0E243E';
const HEADER_H = 48;

const NAV_ICONS: Record<string, ReactNode> = {
  home: <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  structure: (
    <>
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <rect x="2" y="18" width="6" height="4" rx="1" />
      <rect x="9" y="18" width="6" height="4" rx="1" />
      <rect x="16" y="18" width="6" height="4" rx="1" />
      <path d="M12 6v4M5 18v-4h14v4M12 14v4" />
    </>
  ),
  process: (
    <>
      <circle cx="5" cy="6" r="3" />
      <circle cx="19" cy="6" r="3" />
      <circle cx="12" cy="18" r="3" />
      <path d="M8 6h8M5 9v3l7 3M19 9v3l-7 3" />
    </>
  ),
  information: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </>
  ),
  compliance: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  tech: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
    </>
  ),
  reputation: (
    <>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01z" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
  glosario: (
    <>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <path d="M8 7h8M8 11h6" />
    </>
  ),
};

interface NavSection {
  key: string;
  label: string;
  icon: ReactNode;
  discColor: string;
  modules: string[];
  path: string;
}

const NAV_SECTIONS: NavSection[] = [
  {
    key: 'structure',
    label: 'Structure',
    icon: NAV_ICONS.structure,
    discColor: '#1A94BF',
    modules: [...BLOQUE_MODULOS.STRUCTURE],
    path: '/structure',
  },
  {
    key: 'process',
    label: 'Process',
    icon: NAV_ICONS.process,
    discColor: '#2F5496',
    modules: [...BLOQUE_MODULOS.PROCESS],
    path: '/process',
  },
  {
    key: 'information',
    label: 'Information',
    icon: NAV_ICONS.information,
    discColor: '#4472C4',
    modules: [...BLOQUE_MODULOS.INFORMATION],
    path: '/information',
  },
  {
    key: 'compliance',
    label: 'Compliance',
    icon: NAV_ICONS.compliance,
    discColor: '#16507D',
    modules: [...BLOQUE_MODULOS.COMPLIANCE],
    path: '/compliance',
  },
  {
    key: 'tech',
    label: 'Tech',
    icon: NAV_ICONS.tech,
    discColor: '#33BCE1',
    modules: ['M8', ...BLOQUE_MODULOS.TECH],
    path: '/tech',
  },
  {
    key: 'reputation',
    label: 'Reputation',
    icon: NAV_ICONS.reputation,
    discColor: '#0E243E',
    modules: [...BLOQUE_MODULOS.REPUTATION],
    path: '/reputation',
  },
];

function isSectionActive(s: NavSection, p: string) {
  return p === s.path || p.startsWith(s.path + '/');
}
function sectionVisible(m: string[], a: string[] | undefined) {
  return !a || a.length === 0 || m.length === 0 || m.some((x) => a.includes(x));
}

const LANG_LABELS: Record<Lang, string> = { es: 'Espa\u00f1ol', en: 'English', zh: '\u4e2d\u6587' };

// SVG flags — render on all platforms
const FLAG_SVG: Record<Lang, ReactNode> = {
  es: (
    <svg width="22" height="15" viewBox="0 0 20 14" style={{ borderRadius: 2, display: 'block' }}>
      <rect width="20" height="14" fill="#C60B1E" />
      <rect y="3.5" width="20" height="7" fill="#FFC400" />
    </svg>
  ),
  en: (
    <svg width="22" height="15" viewBox="0 0 60 30" style={{ borderRadius: 2, display: 'block' }}>
      <rect width="60" height="30" fill="#00247D" />
      <path d="M0 0L60 30M60 0L0 30" stroke="#fff" strokeWidth="6" />
      <path d="M0 0L60 30M60 0L0 30" stroke="#CF142B" strokeWidth="2" />
      <path d="M30 0V30M0 15H60" stroke="#fff" strokeWidth="10" />
      <path d="M30 0V30M0 15H60" stroke="#CF142B" strokeWidth="6" />
    </svg>
  ),
  zh: (
    <svg width="22" height="15" viewBox="0 0 20 14" style={{ borderRadius: 2, display: 'block' }}>
      <rect width="20" height="14" fill="#DE2910" />
      <polygon
        points="4,2 4.6,3.8 6.5,3.8 5,4.9 5.5,6.7 4,5.6 2.5,6.7 3,4.9 1.5,3.8 3.4,3.8"
        fill="#FFDE00"
      />
    </svg>
  ),
};

// Separator — renders in both expanded and collapsed
function SidebarSep({ collapsed: c }: { collapsed?: boolean }) {
  return (
    <div
      style={{
        height: 1,
        background: 'rgba(255,255,255,0.08)',
        margin: c ? '8px 6px' : '8px 16px',
        transition: 'margin 0.3s cubic-bezier(0.4,0,0.2,1)',
      }}
    />
  );
}

// ======== HEADER ========
function AppShellHeader() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const subRouteLabels: Record<string, string> = useMemo(
    () => ({
      explorador: t.workspace.exploradorDocumentos,
      organigrama: t.structure?.tabs?.orgChart ?? 'Organigrama',
      organizaciones: t.structure?.tabs?.organizations ?? 'Organizaciones',
      gobernanza: t.structure?.tabs?.governance ?? 'Gobernanza',
      auditoria: t.compliance?.tabs?.normative ?? 'Auditor\u00eda',
      settings: t.sidebar.settings,
    }),
    [t],
  );

  const breadcrumb = useMemo(() => {
    const pathname = location.pathname;
    const parts: { label: string; path?: string }[] = [];
    const pt = t.sidebar.pageTitle as Record<string, string>;
    if (pathname.startsWith('/process-map')) {
      parts.push({ label: pt.processMap || t.processMap.title });
      return parts;
    }
    if (pathname.startsWith('/documento/')) {
      const codigo = pathname.split('/documento/')[1]?.split('/')[0] || '';
      parts.push({ label: t.process.documentos });
      if (codigo) parts.push({ label: decodeURIComponent(codigo) });
      return parts;
    }
    if (pathname.startsWith('/proceso/')) {
      const codigo = pathname.split('/proceso/')[1]?.split('/')[0] || '';
      parts.push({ label: pt.process || t.sidebar.process, path: '/process' });
      if (codigo) parts.push({ label: decodeURIComponent(codigo) });
      return parts;
    }
    if (pathname.startsWith('/admin')) {
      parts.push({ label: pt.settings || t.sidebar.settings, path: '/admin' });
      const sub = pathname.split('/admin/')[1];
      if (sub && sub !== 'settings')
        parts.push({ label: sub.charAt(0).toUpperCase() + sub.slice(1) });
      return parts;
    }
    if (pathname.startsWith('/apps/')) {
      parts.push({ label: 'Apps' });
      const sub = pathname.split('/apps/')[1]?.split('/')[0] || '';
      if (sub) parts.push({ label: sub.charAt(0).toUpperCase() + sub.slice(1) });
      return parts;
    }
    const disc = NAV_SECTIONS.find((s) => pathname === s.path || pathname.startsWith(s.path + '/'));
    if (disc) {
      parts.push({ label: pt[disc.key] || disc.label, path: disc.path });
      const rest = pathname.slice(disc.path.length + 1);
      if (rest) {
        const subKey = rest.split('/')[0];
        parts.push({
          label: subRouteLabels[subKey] || subKey.charAt(0).toUpperCase() + subKey.slice(1),
        });
      }
      return parts;
    }
    if (pathname !== '/') parts.push({ label: 'V\u00b7IANet Governance' });
    return parts;
  }, [location.pathname, t, subRouteLabels]);

  const sep = (
    <span
      style={{ color: 'rgba(255,255,255,0.35)', margin: '0 8px', fontSize: 12, userSelect: 'none' }}
    >
      {'>'}
    </span>
  );

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        height: HEADER_H,
        minHeight: HEADER_H,
        backgroundColor: '#0E243E',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        flexShrink: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
      }}
    >
      {/* Logo V·IAnet centrado — link a portal de inicio */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <img
          src="/img/Logo-VIAnet-Blanco.svg"
          alt="V·IAnet Governance"
          onClick={() => navigate('/process-map')}
          style={{
            height: 26,
            display: 'block',
            cursor: 'pointer',
            pointerEvents: 'auto',
            opacity: 0.95,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLImageElement).style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLImageElement).style.opacity = '0.95';
          }}
        />
      </div>

      {/* Breadcrumb */}
      <div
        style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}
      >
        {breadcrumb.map((crumb, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {i > 0 && sep}
            {crumb.path ? (
              <span
                onClick={() => navigate(crumb.path!)}
                style={{
                  fontSize: 15,
                  fontWeight: i === 0 ? 600 : 400,
                  color: i === breadcrumb.length - 1 ? '#fff' : 'rgba(255,255,255,0.65)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color =
                    i === breadcrumb.length - 1 ? '#fff' : 'rgba(255,255,255,0.65)';
                }}
              >
                {crumb.label}
              </span>
            ) : (
              <span
                style={{
                  fontSize: 15,
                  fontWeight: i === breadcrumb.length - 1 ? 600 : 400,
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {crumb.label}
              </span>
            )}
          </span>
        ))}
      </div>

      {/* Wizard shortcut eliminado en 0566 — el onboarding ahora vive en tab Empresa */}

      {/* Notifications */}
      <NotificationBell />

      {/* User menu — avatar + dropdown (info, backoffice, cambiar org, logout) */}
      <UserMenu />
    </header>
  );
}

// ======== APP SHELL ========
export function AppShell() {
  const { tenant } = useTenant();
  const { organizaciones, selectedOrgId, setSelectedOrgId } = useOrganizacion();
  const { t, lang, setLang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const modulosActivos = tenant?.modulosActivos;
  const sidebarW = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;
  const homeActive = location.pathname === '/' || location.pathname.startsWith('/process-map');

  const cycleLang = () => {
    const langs: Lang[] = ['es', 'en', 'zh'];
    const idx = langs.indexOf(lang);
    setLang(langs[(idx + 1) % langs.length]);
  };

  // Icon padding-left = (SIDEBAR_COLLAPSED - icon_size) / 2 - margin
  // (56 - 26) / 2 - 4 = 11px collapsed. Expanded: 20px.
  const navPadL = collapsed ? 11 : 20;

  function navBtn(
    key: string,
    label: string,
    icon: ReactNode,
    active: boolean,
    onClick: () => void,
  ) {
    const hovered = hoveredNav === key;
    return (
      <button
        key={key}
        onClick={onClick}
        onMouseEnter={() => setHoveredNav(key)}
        onMouseLeave={() => setHoveredNav(null)}
        title={collapsed ? label : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: `12px 8px 12px ${navPadL}px`,
          margin: '3px 4px',
          borderRadius: 8,
          cursor: 'pointer',
          color: active ? '#fff' : 'rgba(255,255,255,0.55)',
          transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1), background 0.15s',
          minHeight: 48,
          position: 'relative',
          fontSize: 14,
          fontWeight: active ? 600 : 400,
          border: 'none',
          background: active
            ? 'rgba(255,255,255,0.1)'
            : hovered
              ? 'rgba(255,255,255,0.05)'
              : 'none',
          width: 'calc(100% - 8px)',
          textAlign: 'left',
          fontFamily: 'inherit',
          justifyContent: 'flex-start',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {active && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '15%',
              bottom: '15%',
              width: 3,
              borderRadius: '0 3px 3px 0',
              backgroundColor: '#33BCE1',
            }}
          />
        )}
        <span style={{ flexShrink: 0, display: 'flex' }}>
          <Ic d={icon} s={26} c={active ? '#fff' : 'rgba(255,255,255,0.45)'} />
        </span>
        <span
          style={{ opacity: collapsed ? 0 : 1, transition: 'opacity 0.25s', overflow: 'hidden' }}
        >
          {label}
        </span>
      </button>
    );
  }

  const sidebarStyle: CSSProperties = {
    width: isMobile ? SIDEBAR_EXPANDED : sidebarW,
    minWidth: isMobile ? SIDEBAR_EXPANDED : sidebarW,
    height: '100vh',
    position: 'fixed',
    left: 0,
    top: 0,
    backgroundColor: SIDEBAR_BG,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 200,
    boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
    transform: isMobile && !mobileOpen ? 'translateX(-100%)' : 'translateX(0)',
    transition:
      'width 0.3s cubic-bezier(0.4,0,0.2,1), min-width 0.3s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.4,0,0.2,1)',
    overflowX: 'hidden',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        fontFamily: 'inherit',
      }}
    >
      {/* Impersonation banner (prompt 0524) — sticky top, solo visible si isImpersonating */}
      <ImpersonationBanner />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 199,
            display: isMobile && mobileOpen ? 'block' : 'none',
          }}
          onClick={() => setMobileOpen(false)}
        />

        <aside style={sidebarStyle}>
          {/* Logo zone — icon stays at same x position */}
          <div
            style={{
              padding: `18px 8px 14px ${navPadL}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 10,
              flexShrink: 0,
              minHeight: 68,
              transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            <div
              onClick={() => (isMobile ? setMobileOpen(false) : setCollapsed((c) => !c))}
              style={{
                cursor: 'pointer',
                padding: 4,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Ic d={ICON_PATHS.menu} s={20} c="rgba(255,255,255,0.7)" />
            </div>
            {/* 0589 fix #2: branding del tenant en lugar de VALOR\u00b7IA hardcoded.
                Si el tenant tiene logoHeaderUrl/logoUrl/nombreMarca cargados (admin
                /empresa), se renderiza aqui. Fallback al logo VALOR\u00b7IA cuando no
                hay branding configurado (estado inicial post-onboarding). */}
            {(() => {
              const tenantLogoSrc = tenant?.logoHeaderUrl || tenant?.logoUrl || null;
              const fallbackSrc = '/img/Logo-horizontal-Blanco.png';
              const altText = tenant?.nombreMarca || tenant?.nombre || 'VALOR\u00b7IA';
              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    overflow: 'hidden',
                    flex: 1,
                    opacity: collapsed ? 0 : 1,
                    maxWidth: collapsed ? 0 : 180,
                    transition: 'opacity 0.25s',
                  }}
                >
                  <img
                    src={tenantLogoSrc || fallbackSrc}
                    alt={altText}
                    style={{
                      height: tenantLogoSrc ? 36 : 42,
                      width: 'auto',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      objectPosition: 'left',
                    }}
                    onError={(e) => {
                      // Si el logo del tenant esta roto, fallback al de VALOR\u00b7IA
                      (e.currentTarget as HTMLImageElement).src = fallbackSrc;
                    }}
                  />
                  {tenant?.nombreMarca && (
                    <span
                      style={{
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.55)',
                        fontWeight: 500,
                        letterSpacing: '0.3px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {tenant.nombreMarca}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Search */}
          <div style={{ padding: '0 4px 8px', flexShrink: 0 }}>
            <button
              onClick={() => setSearchOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                width: 'calc(100% - 8px)',
                margin: '0 4px',
                padding: `8px 8px 8px ${navPadL}px`,
                borderRadius: 8,
                border: collapsed ? 'none' : '1px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.45)',
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'inherit',
                justifyContent: 'flex-start',
                minHeight: 40,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1), border 0.3s',
              }}
            >
              <span style={{ flexShrink: 0, display: 'flex' }}>
                <Ic d={ICON_PATHS.search} s={18} c="rgba(255,255,255,0.45)" />
              </span>
              <span
                style={{
                  opacity: collapsed ? 0 : 1,
                  transition: 'opacity 0.25s',
                  overflow: 'hidden',
                }}
              >
                {t.search.searchTooltip ?? 'Buscar...'} {'\u2318'}K
              </span>
            </button>
          </div>

          <SidebarSep collapsed={collapsed} />

          {/* Org selector */}
          {!collapsed && organizaciones.length > 1 && (
            <div style={{ padding: '0 12px 8px', flexShrink: 0 }}>
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  borderRadius: 5,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: 11,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                <option value="" style={{ background: SIDEBAR_BG, color: '#fff' }}>
                  {t.sidebar.allOrgs ?? 'Todas las organizaciones'}
                </option>
                {organizaciones.map((o) => (
                  <option key={o.id} value={o.id} style={{ background: SIDEBAR_BG, color: '#fff' }}>
                    {o.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Nav */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 4px' }}>
            {/* Home — imagotipo as icon */}
            <button
              onClick={() => navigate('/')}
              onMouseEnter={() => setHoveredNav('home')}
              onMouseLeave={() => setHoveredNav(null)}
              title={collapsed ? 'V\u00b7IANet' : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: `12px 8px 12px ${navPadL}px`,
                margin: '3px 4px',
                borderRadius: 8,
                cursor: 'pointer',
                color: homeActive ? '#fff' : 'rgba(255,255,255,0.55)',
                transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1), background 0.15s',
                minHeight: 48,
                position: 'relative',
                fontSize: 14,
                fontWeight: homeActive ? 600 : 400,
                border: 'none',
                background: homeActive
                  ? 'rgba(255,255,255,0.1)'
                  : hoveredNav === 'home'
                    ? 'rgba(255,255,255,0.05)'
                    : 'none',
                width: 'calc(100% - 8px)',
                textAlign: 'left',
                fontFamily: 'inherit',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {homeActive && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '15%',
                    bottom: '15%',
                    width: 3,
                    borderRadius: '0 3px 3px 0',
                    backgroundColor: '#33BCE1',
                  }}
                />
              )}
              <img
                src="/img/Imagotipo-blanco.png"
                alt="V\u00b7IANet"
                style={{
                  height: 26,
                  width: 26,
                  objectFit: 'contain',
                  opacity: homeActive ? 1 : 0.55,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  opacity: collapsed ? 0 : 1,
                  transition: 'opacity 0.25s',
                  overflow: 'hidden',
                }}
              >
                V{'\u00b7'}IANet
              </span>
            </button>

            <SidebarSep collapsed={collapsed} />

            {/* Action Center */}
            <button
              onClick={() => navigate('/sign/action-center')}
              onMouseEnter={() => setHoveredNav('action-center')}
              onMouseLeave={() => setHoveredNav(null)}
              title={collapsed ? (t.sidebar.actionCenter ?? 'Action Center') : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: `12px 8px 12px ${navPadL}px`,
                margin: '3px 4px',
                borderRadius: 8,
                cursor: 'pointer',
                color: location.pathname.startsWith('/sign/action-center')
                  ? '#fff'
                  : 'rgba(255,255,255,0.55)',
                fontSize: 14,
                fontWeight: location.pathname.startsWith('/sign/action-center') ? 600 : 400,
                border: 'none',
                background: location.pathname.startsWith('/sign/action-center')
                  ? 'rgba(255,255,255,0.1)'
                  : hoveredNav === 'action-center'
                    ? 'rgba(255,255,255,0.05)'
                    : 'none',
                width: 'calc(100% - 8px)',
                textAlign: 'left',
                fontFamily: 'inherit',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1), background 0.15s',
                minHeight: 48,
                position: 'relative',
              }}
            >
              {location.pathname.startsWith('/sign/action-center') && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '15%',
                    bottom: '15%',
                    width: 3,
                    borderRadius: '0 3px 3px 0',
                    backgroundColor: '#33BCE1',
                  }}
                />
              )}
              <span style={{ flexShrink: 0, display: 'flex' }}>
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={
                    location.pathname.startsWith('/sign/action-center')
                      ? '#fff'
                      : 'rgba(255,255,255,0.45)'
                  }
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
              </span>
              <span
                style={{
                  opacity: collapsed ? 0 : 1,
                  transition: 'opacity 0.25s',
                  overflow: 'hidden',
                  flex: 1,
                }}
              >
                {t.sidebar.actionCenter ?? 'Action Center'}
              </span>
              {/* 0615+: badge siempre visible. Inline cuando expandido,
                  overlay (esquina superior derecha del icono) cuando colapsado. */}
              <ActionCenterBadge variant={collapsed ? 'overlay' : 'inline'} />
            </button>

            <SidebarSep collapsed={collapsed} />

            {/* Disciplines */}
            {NAV_SECTIONS.filter((s) => sectionVisible(s.modules, modulosActivos)).map((section) =>
              navBtn(
                section.key,
                (t.sidebar as unknown as Record<string, string>)[section.key] || section.label,
                section.icon,
                isSectionActive(section, location.pathname),
                () => navigate(section.path),
              ),
            )}
          </div>

          {/* Bottom section */}
          <div style={{ flexShrink: 0 }}>
            <SidebarSep collapsed={collapsed} />

            {/* Language — flag + label */}
            <button
              onClick={cycleLang}
              onMouseEnter={() => setHoveredNav('lang')}
              onMouseLeave={() => setHoveredNav(null)}
              title={LANG_LABELS[lang]}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: `12px 8px 12px ${navPadL}px`,
                margin: '3px 4px',
                borderRadius: 8,
                cursor: 'pointer',
                minHeight: 48,
                border: 'none',
                fontSize: 14,
                fontFamily: 'inherit',
                color: 'rgba(255,255,255,0.55)',
                background: hoveredNav === 'lang' ? 'rgba(255,255,255,0.05)' : 'none',
                width: 'calc(100% - 8px)',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1), background 0.15s',
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 26,
                  height: 26,
                }}
              >
                {FLAG_SVG[lang]}
              </span>
              <span
                style={{
                  opacity: collapsed ? 0 : 1,
                  transition: 'opacity 0.25s',
                  overflow: 'hidden',
                }}
              >
                {LANG_LABELS[lang]}
              </span>
            </button>

            {/* Glosario — transversal */}
            {navBtn(
              'glosario',
              t.glosario.title,
              NAV_ICONS.glosario,
              location.pathname.startsWith('/glosario'),
              () => navigate('/glosario'),
            )}

            {/* Settings */}
            {sectionVisible(['M8'], modulosActivos) &&
              navBtn(
                'admin',
                t.sidebar.settings,
                NAV_ICONS.settings,
                location.pathname.startsWith('/admin'),
                () => navigate('/admin'),
              )}

            <SidebarSep collapsed={collapsed} />

            {/* Footer — compact in collapsed */}
            <div
              style={{
                padding: collapsed ? '6px 2px' : '8px 16px',
                textAlign: 'center',
                flexShrink: 0,
                transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  fontSize: collapsed ? 7 : 9,
                  color: 'rgba(255,255,255,0.2)',
                  marginBottom: collapsed ? 0 : 6,
                  transition: 'font-size 0.3s',
                }}
              >
                {collapsed ? (
                  <>
                    pw
                    <br />
                    by
                  </>
                ) : (
                  'Powered by'
                )}
              </div>
              <img
                src="/img/Logo-horizontal-Blanco.png"
                alt="VALOR\u00b7IA"
                style={{
                  height: collapsed ? 0 : 28,
                  width: 'auto',
                  opacity: collapsed ? 0 : 0.35,
                  display: 'block',
                  margin: '0 auto',
                  transition: 'height 0.3s, opacity 0.25s',
                  overflow: 'hidden',
                }}
              />
            </div>
          </div>
        </aside>

        {/* Main */}
        <div
          style={{
            marginLeft: isMobile ? 0 : sidebarW,
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
            transition: 'margin-left 0.3s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          {isMobile && !mobileOpen && (
            <div
              onClick={() => setMobileOpen(true)}
              style={{
                position: 'fixed',
                top: 12,
                left: 12,
                zIndex: 150,
                cursor: 'pointer',
                padding: 8,
                borderRadius: 8,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <Ic d={ICON_PATHS.menu} s={22} c="#fff" />
            </div>
          )}

          <AppShellHeader />

          <main
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#F8F8F8',
              position: 'relative',
            }}
          >
            <Outlet />
          </main>
        </div>
      </div>

      {searchOpen && <QuickSearchModal onClose={() => setSearchOpen(false)} />}

      {/* 0531: footer minimalista con versión + commit SHA · click copia al portapapeles */}
      <AppVersionBadge />
    </div>
  );
}
